import { Player } from '@/types';
import { wyscoutMatchesAPI } from '@/services/api';
import { mapWyscoutLineup } from '@/utils/wyscoutFormationMapper';
import { PlayerAnnotation } from '../FormationPitch';
import { GoalEvent, BookingEvent, MatchBrowserResult, Substitution } from './types';
import { parseTeamNames } from './helpers';

export interface MatchInfo {
  homeTeamName: string;
  awayTeamName: string;
  homeScore: number;
  awayScore: number;
  dateUtc: string;
  venue: string;
  substitutions: Substitution[];
  playerNames: Map<number, string>;
  goals: GoalEvent[];
  bookings: BookingEvent[];
}

export type LoadMatchOutcome =
  | { kind: 'error'; error: string }
  | { kind: 'success'; matchInfo: MatchInfo; result: MatchBrowserResult };

export const loadMatch = async (matchId: number, teamId: number | null): Promise<LoadMatchOutcome> => {
  console.log(`[MatchBrowser] Loading match detail for wyscoutMatchId=${matchId}...`);
  const detail = await wyscoutMatchesAPI.getStoredMatch(matchId);
  const match = detail.data;
  console.log(`[MatchBrowser] Match loaded:`, {
    label: match.label,
    homeTeamId: match.homeTeamId,
    awayTeamId: match.awayTeamId,
    ourTeamId: teamId,
    homeFormation: match.homeFormationSchema,
    awayFormation: match.awayFormationSchema,
    homeHasFormation: match.homeHasFormation,
    awayHasFormation: match.awayHasFormation,
    homeLineupCount: match.homeLineup?.length,
    awayLineupCount: match.awayLineup?.length,
  });

  const isHome = teamId !== null && match.homeTeamId === teamId;
  console.log(`[MatchBrowser] We are ${isHome ? 'HOME' : 'AWAY'} (teamId=${teamId})`);

  const ourLineup = isHome ? match.homeLineup : match.awayLineup;
  const ourBench = isHome ? match.homeBench : match.awayBench;
  const ourSubs = isHome ? match.homeSubstitutions : match.awaySubstitutions;
  const ourSchema = isHome ? match.homeFormationSchema : match.awayFormationSchema;

  console.log(`[MatchBrowser] Our data: schema="${ourSchema}", lineup=${ourLineup?.length || 0}, bench=${ourBench?.length || 0}, subs=${ourSubs?.length || 0}`);

  if (!ourLineup || ourLineup.length === 0) {
    console.error('[MatchBrowser] No lineup data available for our team!');
    return { kind: 'error', error: 'No lineup data available for this match' };
  }

  if (!ourSchema) {
    console.warn('[MatchBrowser] No formation schema available, will use default');
  }

  console.log(`[MatchBrowser] Lineup details:`, ourLineup.map((p) => `#${p.shirtNumber} ${p.position} (id:${p.playerId})`));

  // Derive bench from substitutions if the explicit bench is empty.
  // Any playerIn from a sub who isn't in the starting lineup is a bench player.
  let effectiveBench = ourBench || [];
  if (effectiveBench.length === 0 && ourSubs && ourSubs.length > 0) {
    const lineupIds = new Set(ourLineup.map((p) => p.playerId));
    const derivedBenchIds = new Set<number>();
    for (const sub of ourSubs) {
      if (!lineupIds.has(sub.playerIn) && !derivedBenchIds.has(sub.playerIn)) {
        derivedBenchIds.add(sub.playerIn);
        effectiveBench.push({
          playerId: sub.playerIn,
          shirtNumber: 0,
          position: '',
        } as typeof ourLineup[0]);
      }
    }
    console.log(`[MatchBrowser] Derived ${effectiveBench.length} bench players from ${ourSubs.length} substitutions`);
  }

  // Resolve players
  const allPlayers = [...ourLineup, ...effectiveBench].map((p) => ({
    wyscoutPlayerId: p.playerId,
    name: p.shirtNumber ? `#${p.shirtNumber}` : '',
    shortName: p.shirtNumber ? `#${p.shirtNumber}` : '',
    position: p.position,
  }));

  const playerLookup = new Map<number, Player>();
  const playerNames = new Map<number, string>();

  try {
    console.log(`[MatchBrowser] Resolving ${allPlayers.length} players...`);
    const resolved = await wyscoutMatchesAPI.resolvePlayers(allPlayers);
    console.log(`[MatchBrowser] Resolve response:`, resolved.data);
    if (resolved.data) {
      for (const [wyscoutIdStr, info] of Object.entries(resolved.data)) {
        const wyscoutId = Number(wyscoutIdStr);
        // Use the name returned from the backend (from DB), fall back to wyscoutId
        const playerName = info.name || `Player ${wyscoutId}`;
        const player: Player = {
          id: info.playerId,
          name: playerName,
          age: 0,
          position: 'Forward',
          createdBy: '',
          createdAt: '',
          updatedAt: '',
          wyscoutPlayerId: wyscoutId,
        };
        playerLookup.set(wyscoutId, player);
        playerNames.set(wyscoutId, playerName);
      }
      console.log(`[MatchBrowser] Resolved ${playerLookup.size} players`);
    }
  } catch (resolveErr) {
    console.warn('[MatchBrowser] Player resolve failed, using synthetic players:', resolveErr);
  }

  // Fill in unresolved lineup players with synthetic entries
  for (const lp of [...ourLineup, ...(ourBench || [])]) {
    if (!playerLookup.has(lp.playerId)) {
      const fallbackName = lp.shirtNumber ? `#${lp.shirtNumber}` : `Player ${lp.playerId}`;
      playerLookup.set(lp.playerId, {
        id: `wyscout-${lp.playerId}`,
        name: fallbackName,
        age: 0,
        position: 'Forward',
        createdBy: '',
        createdAt: '',
        updatedAt: '',
        wyscoutPlayerId: lp.playerId,
      });
    }
    if (!playerNames.has(lp.playerId)) {
      playerNames.set(lp.playerId, lp.shirtNumber ? `#${lp.shirtNumber}` : `Player ${lp.playerId}`);
    }
  }

  console.log(`[MatchBrowser] Total playerLookup size: ${playerLookup.size}`);
  console.log(`[MatchBrowser] Calling mapWyscoutLineup with schema="${ourSchema || '4-3-3'}"...`);

  const mapping = mapWyscoutLineup(ourSchema || '4-3-3', ourLineup, playerLookup);

  if (mapping) {
    console.log(`[MatchBrowser] Mapping successful: formation="${mapping.formationName}", positions filled=${Object.keys(mapping.positionMap).length}`);

    // Build a reverse lookup: wyscoutPlayerId -> positionId
    const wyscoutToPos = new Map<number, string>();
    for (const [posId, player] of Object.entries(mapping.positionMap)) {
      if (player?.wyscoutPlayerId) {
        wyscoutToPos.set(player.wyscoutPlayerId, posId);
      }
    }

    // Fetch match events to extract goals, assists, bookings
    const goals: GoalEvent[] = [];
    const bookings: BookingEvent[] = [];
    const annotationMap: Record<string, PlayerAnnotation> = {};
    // Track bench players derived from events (when explicit bench is empty)
    const benchPlayerIds = new Set(effectiveBench.map((p) => p.playerId));
    const extraBenchPlayers: Player[] = [];

    // Helper to get or init annotation for a position
    const getAnnotation = (posId: string): PlayerAnnotation => {
      if (!annotationMap[posId]) annotationMap[posId] = {};
      return annotationMap[posId];
    };

    try {
      console.log(`[MatchBrowser] Fetching match events for matchId=${matchId}...`);
      const eventsRes = await wyscoutMatchesAPI.getMatchEvents(matchId);
      // Events could be in eventsRes.events or eventsRes directly as array
      const events: Array<{
        minute: number;
        player?: { id: number; name?: string };
        team?: { id: number };
        type: { primary: string; secondary?: string[] };
        shot?: { isGoal?: boolean };
        pass?: { recipient?: { id: number; name?: string } };
        infraction?: { yellowCard?: boolean; redCard?: boolean };
      }> = eventsRes?.events || [];

      console.log(`[MatchBrowser] Got ${events.length} total events`);

      const ourTeamId = isHome ? match.homeTeamId : match.awayTeamId;

      for (const evt of events) {
        if (!evt.team || evt.team.id !== ourTeamId) continue;
        const playerId = evt.player?.id;
        const posId = playerId ? wyscoutToPos.get(playerId) : undefined;
        const ownGoal = evt.type.primary === 'own_goal' && (evt.type.secondary || []).includes('goal');

        // Goals
        if (!ownGoal && evt.shot?.isGoal && playerId) {
          const scorerName = playerNames.get(playerId) || evt.player?.name || `Player ${playerId}`;
          const goalEvt: GoalEvent = { playerName: scorerName, minute: evt.minute };

          // Find assist: look for an event with type.secondary including 'goal_assist' near same minute
          // The assist is typically a pass right before the goal - check related events
          goals.push(goalEvt);

          if (posId) {
            const a = getAnnotation(posId);
            a.goals = (a.goals || 0) + 1;
          }
        }

        // Assists (type.secondary includes 'goal_assist' or similar)
        const secondary = (evt.type.secondary || []).map(s => s.toLowerCase());
        if (secondary.includes('goal_assist') && playerId) {
          const assistName = playerNames.get(playerId) || evt.player?.name || `Player ${playerId}`;
          // Attach assist to the most recent goal
          if (goals.length > 0 && !goals[goals.length - 1].assistName) {
            goals[goals.length - 1].assistName = assistName;
          }
          if (posId) {
            const a = getAnnotation(posId);
            a.assists = (a.assists || 0) + 1;
          }
        }

        // Bookings
        if (evt.infraction?.yellowCard && playerId) {
          const name = playerNames.get(playerId) || evt.player?.name || `Player ${playerId}`;
          bookings.push({ playerName: name, minute: evt.minute, type: 'yellow' });
          if (posId) {
            getAnnotation(posId).yellowCard = true;
          }
        }
        if (evt.infraction?.redCard && playerId) {
          const name = playerNames.get(playerId) || evt.player?.name || `Player ${playerId}`;
          bookings.push({ playerName: name, minute: evt.minute, type: 'red' });
          if (posId) {
            getAnnotation(posId).redCard = true;
          }
        }
      }

      console.log(`[MatchBrowser] Extracted: ${goals.length} goals, ${bookings.length} bookings for our team`);

      // If bench is still empty, derive bench players from substitution events
      if (benchPlayerIds.size === 0) {
        const lineupPlayerIds = new Set(ourLineup.map((p) => p.playerId));
        for (const evt of events) {
          if (!evt.team || evt.team.id !== ourTeamId) continue;
          const secondary = (evt.type.secondary || []).map(s => s.toLowerCase());
          if (evt.type.primary?.toLowerCase() === 'substitution' || secondary.includes('substitution')) {
            // The player performing the event could be the sub-in
            const pid = evt.player?.id;
            if (pid && !lineupPlayerIds.has(pid) && !benchPlayerIds.has(pid)) {
              benchPlayerIds.add(pid);
              const pName = evt.player?.name || `Player ${pid}`;
              // Resolve this player
              if (!playerLookup.has(pid)) {
                try {
                  const resolveRes = await wyscoutMatchesAPI.resolvePlayers([{
                    wyscoutPlayerId: pid, name: pName, shortName: pName, position: '',
                  }]);
                  const info = resolveRes.data?.[String(pid)];
                  if (info) {
                    const player: Player = {
                      id: info.playerId,
                      name: info.name || pName,
                      age: 0, position: 'Forward',
                      createdBy: '', createdAt: '', updatedAt: '',
                      wyscoutPlayerId: pid,
                    };
                    playerLookup.set(pid, player);
                    playerNames.set(pid, player.name);
                  }
                } catch { /* non-critical */ }
              }
              const resolved = playerLookup.get(pid);
              if (resolved) extraBenchPlayers.push(resolved);
            }
          }
        }
        if (extraBenchPlayers.length > 0) {
          console.log(`[MatchBrowser] Derived ${extraBenchPlayers.length} bench players from substitution events`);
        }
      }
    } catch (evtErr) {
      console.warn('[MatchBrowser] Failed to fetch match events (non-critical):', evtErr);
    }

    // Add substitution annotations
    for (const sub of (ourSubs || [])) {
      const outPosId = wyscoutToPos.get(sub.playerOut);
      if (outPosId) {
        getAnnotation(outPosId).subbedOff = sub.minute;
      }
      const inPosId = wyscoutToPos.get(sub.playerIn);
      if (inPosId) {
        getAnnotation(inPosId).subbedOn = sub.minute;
      }
    }

    const names = parseTeamNames(match.label);

    const matchInfo: MatchInfo = {
      homeTeamName: names.home,
      awayTeamName: names.away,
      homeScore: match.homeScore,
      awayScore: match.awayScore,
      dateUtc: match.dateUtc,
      venue: match.venue,
      substitutions: ourSubs || [],
      playerNames,
      goals,
      bookings,
    };

    // Map bench players to Player objects using playerLookup
    const benchFromData: Player[] = effectiveBench
      .map((bp) => playerLookup.get(bp.playerId))
      .filter((p): p is Player => p !== undefined);
    // Combine bench from stored data + bench derived from events
    const seenBenchIds = new Set(benchFromData.map((p) => p.wyscoutPlayerId));
    const benchPlayerObjects = [
      ...benchFromData,
      ...extraBenchPlayers.filter((p) => !seenBenchIds.has(p.wyscoutPlayerId)),
    ];
    console.log(`[MatchBrowser] Bench players: ${benchFromData.length} from data + ${extraBenchPlayers.length} from events = ${benchPlayerObjects.length} total`);

    const result: MatchBrowserResult = {
      formationName: mapping.formationName,
      positionMap: mapping.positionMap,
      annotations: annotationMap,
      benchPlayers: benchPlayerObjects,
      substitutions: ourSubs || [],
      matchLabel: match.label,
      matchDate: match.dateUtc,
    };
    console.log(`[MatchBrowser] Calling onMatchLoaded with formationName=${result.formationName}, positions=${Object.keys(result.positionMap).length}, annotations=${Object.keys(annotationMap).length}, bench=${benchPlayerObjects.length}`);
    return { kind: 'success', matchInfo, result };
  } else {
    console.error('[MatchBrowser] mapWyscoutLineup returned null! This should not happen with the fallback logic.');
    return { kind: 'error', error: 'Failed to map formation - please check console' };
  }
};
